import { CstParser } from "chevrotain";

import {
  AttributeName,
  Caret,
  Colon,
  Comma,
  Equals,
  FunctionName,
  GreaterEquals,
  GreaterThan,
  LeftBrace,
  LeftBracket,
  LeftParenthesis,
  LessEquals,
  LessThan,
  LogicalAnd,
  LogicalOr,
  Minus,
  NotEquals,
  NumberLiteral,
  Operator,
  Percent,
  Plus,
  RightBrace,
  RightBracket,
  RightParenthesis,
  Slash,
  Star,
  StringLiteral,
  allTokens,
} from "@/helpers/parser/lexer";

class FormulaParser extends CstParser {
  constructor() {
    super(allTokens, { recoveryEnabled: true });
    this.performSelfAnalysis();
  }

  public expression = this.RULE("expression", () => {
    this.SUBRULE(this.logicalExpression);
  });

  private logicalExpression = this.RULE("logicalExpression", () => {
    this.SUBRULE(this.comparisonExpression, { LABEL: "lhs" });
    this.MANY(() => {
      this.OR([
        { ALT: () => this.CONSUME(LogicalAnd) },
        { ALT: () => this.CONSUME(LogicalOr) },
      ]);
      this.SUBRULE2(this.comparisonExpression, { LABEL: "rhs" });
    });
  });

  private comparisonExpression = this.RULE("comparisonExpression", () => {
    this.SUBRULE(this.additionExpression, { LABEL: "lhs" });
    this.MANY(() => {
      this.OR([
        { ALT: () => this.CONSUME(Equals) },
        { ALT: () => this.CONSUME(NotEquals) },
        { ALT: () => this.CONSUME(LessThan) },
        { ALT: () => this.CONSUME(GreaterThan) },
        { ALT: () => this.CONSUME(LessEquals) },
        { ALT: () => this.CONSUME(GreaterEquals) },
      ]);
      this.SUBRULE2(this.additionExpression, { LABEL: "rhs" });
    });
  });

  private additionExpression = this.RULE("additionExpression", () => {
    this.SUBRULE(this.multiplicationExpression, { LABEL: "lhs" });
    this.MANY(() => {
      this.OR([
        { ALT: () => this.CONSUME(Plus) },
        { ALT: () => this.CONSUME(Minus) },
      ]);
      this.SUBRULE2(this.multiplicationExpression, { LABEL: "rhs" });
    });
  });

  private multiplicationExpression = this.RULE(
    "multiplicationExpression",
    () => {
      this.SUBRULE(this.exponentiationExpression, { LABEL: "lhs" });
      this.MANY(() => {
        this.OR([
          { ALT: () => this.CONSUME(Star) },
          { ALT: () => this.CONSUME(Slash) },
          { ALT: () => this.CONSUME(Percent) },
        ]);
        this.SUBRULE2(this.exponentiationExpression, { LABEL: "rhs" });
      });
    }
  );

  private exponentiationExpression = this.RULE(
    "exponentiationExpression",
    () => {
      this.SUBRULE(this.atomicExpression, { LABEL: "lhs" });
      this.MANY(() => {
        this.CONSUME(Caret);
        this.SUBRULE2(this.atomicExpression, { LABEL: "rhs" });
      });
    }
  );

  private unaryExpression = this.RULE("unaryExpression", () => {
    this.CONSUME(Minus);
    this.SUBRULE(this.numberLiteral);
  });

  private atomicExpression = this.RULE("atomicExpression", () => {
    this.OR([
      { ALT: () => this.SUBRULE(this.unaryExpression) },
      { ALT: () => this.SUBRULE(this.functionCall) },
      { ALT: () => this.SUBRULE(this.parenthesizedExpression) },
      { ALT: () => this.SUBRULE(this.numberLiteral) },
      { ALT: () => this.SUBRULE(this.stringLiteral) },
      { ALT: () => this.SUBRULE(this.attributeReference) },
      { ALT: () => this.SUBRULE(this.uppercaseIdentifier) },
    ]);
  });

  private uppercaseIdentifier = this.RULE("uppercaseIdentifier", () => {
    this.CONSUME(FunctionName);
  });

  private numberLiteral = this.RULE("numberLiteral", () => {
    this.CONSUME(NumberLiteral);
  });

  private stringLiteral = this.RULE("stringLiteral", () => {
    this.CONSUME(StringLiteral);
  });

  private attributeReference = this.RULE("attributeReference", () => {
    this.CONSUME(AttributeName);
    this.OPTION(() => {
      this.SUBRULE(this.attributeContext);
    });
  });

  private attributeContext = this.RULE("attributeContext", () => {
    this.CONSUME(LeftBrace);
    this.MANY(() => {
      this.OR([
        { ALT: () => this.SUBRULE(this.nestedBraceGroup) },
        { ALT: () => this.SUBRULE(this.nestedBracketGroup) },
        { ALT: () => this.CONSUME(AttributeName) },
        { ALT: () => this.CONSUME(NumberLiteral) },
        { ALT: () => this.CONSUME(StringLiteral) },
        { ALT: () => this.CONSUME(Comma) },
        { ALT: () => this.CONSUME(Colon) },
        { ALT: () => this.CONSUME(Operator) },
      ]);
    });
    this.CONSUME2(RightBrace);
  });

  private nestedBraceGroup = this.RULE("nestedBraceGroup", () => {
    this.CONSUME(LeftBrace);
    this.MANY(() => {
      this.OR([
        { ALT: () => this.SUBRULE(this.nestedBraceGroup) },
        { ALT: () => this.SUBRULE(this.nestedBracketGroup) },
        { ALT: () => this.CONSUME(AttributeName) },
        { ALT: () => this.CONSUME(NumberLiteral) },
        { ALT: () => this.CONSUME(StringLiteral) },
        { ALT: () => this.CONSUME(Comma) },
        { ALT: () => this.CONSUME(Colon) },
        { ALT: () => this.CONSUME(Operator) },
      ]);
    });
    this.CONSUME(RightBrace);
  });

  private nestedBracketGroup = this.RULE("nestedBracketGroup", () => {
    this.CONSUME(LeftBracket);
    this.MANY(() => {
      this.OR([
        { ALT: () => this.SUBRULE(this.nestedBraceGroup) },
        { ALT: () => this.SUBRULE2(this.nestedBracketGroup) },
        { ALT: () => this.CONSUME(AttributeName) },
        { ALT: () => this.CONSUME(NumberLiteral) },
        { ALT: () => this.CONSUME(StringLiteral) },
        { ALT: () => this.CONSUME(Comma) },
        { ALT: () => this.CONSUME(Colon) },
        { ALT: () => this.CONSUME(Operator) },
      ]);
    });
    this.CONSUME(RightBracket);
  });

  private parenthesizedExpression = this.RULE("parenthesizedExpression", () => {
    this.CONSUME(LeftParenthesis);
    this.SUBRULE(this.expression);
    this.CONSUME(RightParenthesis);
  });

  private functionCall = this.RULE("functionCall", () => {
    this.OR([
      {
        GATE: () => this.LA(2).tokenType === LeftParenthesis,
        ALT: () => this.CONSUME(FunctionName),
      },
      { ALT: () => this.CONSUME(Plus) },
      { ALT: () => this.CONSUME(Minus) },
      { ALT: () => this.CONSUME(Star) },
      { ALT: () => this.CONSUME(Slash) },
      { ALT: () => this.CONSUME(Percent) },
      { ALT: () => this.CONSUME(Caret) },
      { ALT: () => this.CONSUME(LessThan) },
      { ALT: () => this.CONSUME(GreaterThan) },
      { ALT: () => this.CONSUME(Equals) },
      { ALT: () => this.CONSUME(NotEquals) },
      { ALT: () => this.CONSUME(LessEquals) },
      { ALT: () => this.CONSUME(GreaterEquals) },
      { ALT: () => this.CONSUME(LogicalAnd) },
      { ALT: () => this.CONSUME(LogicalOr) },
    ]);
    this.CONSUME(LeftParenthesis);
    this.OPTION(() => {
      this.SUBRULE(this.argumentList);
    });
    this.CONSUME(RightParenthesis);
  });

  private argumentList = this.RULE("argumentList", () => {
    this.SUBRULE(this.expression, { LABEL: "argument" });
    this.MANY(() => {
      this.CONSUME(Comma);
      this.SUBRULE2(this.expression, { LABEL: "argument" });
    });
  });
}

export const parserInstance = new FormulaParser();
